import { Checkbox, Modal, Tooltip } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'

import Loader from '@/components/Atoms/Loader/Loader'
import { useTreeTilesStore } from '@/stores/feed/treeTilesStore'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { fetchCategoriesTree } from '@/utils/api/fetch/fetchEntities'
import {
  getApprovalStatusLabel,
  getStatusBadge,
} from '@/utils/catalog/approvalStatus'

import {
  type BoundCharacteristicCategory,
  buildCategorySelectionMap,
  buildCategorySelectionTree,
  type CategorySelectionNode,
  collectExpandableIds,
  filterCategorySelectionTree,
  flattenVisibleCategoryTree,
  getConflictingCategoryIds,
} from '../characteristicCategoryUtils'
import cn from '../DictionaryDetailsModal.module.scss'

const CONFLICT_TOOLTIP =
  'Категория недоступна: характеристика уже выбрана в этой ветке.'

type CharacteristicCategoryBindingsModalProps = {
  selectedCategories: BoundCharacteristicCategory[]
  onChange: (categories: BoundCharacteristicCategory[]) => void
  readOnly?: boolean
}

type EllipsisTooltipTextProps = {
  className: string
  text: string
}

function EllipsisTooltipText(props: EllipsisTooltipTextProps) {
  const { className, text } = props

  const textRef = useRef<HTMLSpanElement | null>(null)
  const [isOverflowed, setIsOverflowed] = useState(false)

  useEffect(() => {
    const node = textRef.current
    if (!node) return

    const checkOverflow = () => {
      setIsOverflowed(
        node.scrollWidth > node.clientWidth ||
          node.scrollHeight > node.clientHeight
      )
    }

    checkOverflow()

    if (typeof ResizeObserver === 'undefined') return

    const resizeObserver = new ResizeObserver(() => {
      checkOverflow()
    })

    resizeObserver.observe(node)

    return () => {
      resizeObserver.disconnect()
    }
  }, [text])

  const content = (
    <span ref={textRef} className={className}>
      {text}
    </span>
  )

  if (!isOverflowed) return content

  return <Tooltip title={text}>{content}</Tooltip>
}

function CharacteristicCategoryBindingsModal(
  props: CharacteristicCategoryBindingsModalProps
) {
  const { selectedCategories, onChange, readOnly = false } = props

  const [treeNodes, setTreeNodes] = useState<CategorySelectionNode[]>([])
  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [expandedIds, setExpandedIds] = useState<string[]>([])
  const [modal, contextHolder] = Modal.useModal()

  const { categoriesTree, setCategoriesTree } = useTreeTilesStore()

  useEffect(() => {
    const loadCategoryData = async () => {
      if (categoriesTree && categoriesTree.length > 0) {
        setTreeNodes(buildCategorySelectionTree(categoriesTree))
        return
      }

      setLoading(true)
      try {
        const tree = await fetchCategoriesTree()
        setCategoriesTree(tree)
        setTreeNodes(buildCategorySelectionTree(tree))
      } catch (error) {
        handleApiError(error, modal)
      } finally {
        setLoading(false)
      }
    }

    loadCategoryData()
  }, [categoriesTree, modal, setCategoriesTree])

  const selectedIds = useMemo(
    () => selectedCategories.map((category) => category.id),
    [selectedCategories]
  )
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const selectedCategoriesById = useMemo(
    () =>
      new Map(selectedCategories.map((category) => [category.id, category])),
    [selectedCategories]
  )
  const nodesById = useMemo(
    () => buildCategorySelectionMap(treeNodes),
    [treeNodes]
  )
  const filteredTreeNodes = useMemo(
    () => filterCategorySelectionTree(treeNodes, searchValue),
    [treeNodes, searchValue]
  )
  const conflictingIds = useMemo(
    () => getConflictingCategoryIds(selectedIds, nodesById),
    [selectedIds, nodesById]
  )
  const visibleExpandedIds = useMemo(
    () =>
      searchValue.trim()
        ? collectExpandableIds(filteredTreeNodes)
        : new Set(expandedIds),
    [expandedIds, filteredTreeNodes, searchValue]
  )
  const visibleNodes = useMemo(
    () => flattenVisibleCategoryTree(filteredTreeNodes, visibleExpandedIds),
    [filteredTreeNodes, visibleExpandedIds]
  )

  const buildSelectedCategories = (nextSelectedIds: string[]) => {
    return nextSelectedIds.reduce<BoundCharacteristicCategory[]>(
      (accumulator, selectedId) => {
        const selectedCategory = selectedCategoriesById.get(selectedId)
        if (selectedCategory) {
          accumulator.push(selectedCategory)
          return accumulator
        }

        const node = nodesById.get(selectedId)
        if (!node) return accumulator

        accumulator.push({
          id: node.id,
          code: node.code,
          description: node.description,
          status: node.status ?? 'DRAFT',
          parent: node.parentDescription
            ? {
                id: node.parentId,
                parentId: '',
                code: 0,
                description: node.parentDescription,
                status: undefined,
              }
            : null,
        })

        return accumulator
      },
      []
    )
  }

  const handleCategoryToggle = (categoryId: string, checked: boolean) => {
    const nextSelectedIds = checked
      ? selectedIds.includes(categoryId)
        ? selectedIds
        : [...selectedIds, categoryId]
      : selectedIds.filter((id) => id !== categoryId)

    onChange(buildSelectedCategories(nextSelectedIds))
  }

  const handleExpandToggle = (categoryId: string) => {
    setExpandedIds((prevExpandedIds) =>
      prevExpandedIds.includes(categoryId)
        ? prevExpandedIds.filter((id) => id !== categoryId)
        : [...prevExpandedIds, categoryId]
    )
  }

  const renderStatus = (status?: 'DRAFT' | 'APPROVED' | 'REJECTED') => {
    if (!status || status === 'APPROVED') {
      return <span className={cn.categoryStatusApproved}>Утверждена</span>
    }

    return getStatusBadge(status) ?? getApprovalStatusLabel(status) ?? '—'
  }

  return (
    <>
      {contextHolder}
      <div className={cn.categoryEditor}>
        <div className={cn.selectorSearchBlock}>
          <input
            value={searchValue}
            className={cn.selectorSearch}
            placeholder="Поиск по коду, категории, родителю или пути"
            onChange={(event) => setSearchValue(event.target.value)}
          />
        </div>

        {loading ? (
          <div className={cn.selectorLoader}>
            <Loader size={24} />
          </div>
        ) : (
          <div className={cn.categoryList}>
            <div
              className={[cn.categoryListRow, cn.categoryListHeader].join(' ')}
            >
              <div className={cn.categoryCheckboxCell} />
              <div className={cn.categoryCodeCell}>Код</div>
              <div className={cn.categoryNameCell}>Категория</div>
              <div className={cn.categoryParentCell}>Родитель</div>
              <div className={cn.categoryStatusCell}>Статус</div>
            </div>
            {visibleNodes.length > 0 ? (
              <div className={cn.categoryListBody}>
                {visibleNodes.map((node) => {
                  const isChecked = selectedIdSet.has(node.id)
                  const isBlocked = !isChecked && conflictingIds.has(node.id)
                  const isExpanded = visibleExpandedIds.has(node.id)
                  const hasChildren = node.children.length > 0

                  const checkbox = (
                    <Checkbox
                      className={[
                        cn.categoryCheckbox,
                        isChecked ? cn.categoryCheckboxChecked : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      checked={isChecked}
                      disabled={isBlocked || readOnly}
                      onChange={(event) =>
                        handleCategoryToggle(node.id, event.target.checked)
                      }
                    />
                  )

                  return (
                    <div
                      key={node.id}
                      className={[
                        cn.categoryListRow,
                        cn.categoryStaticRow,
                        isChecked ? cn.categoryListRowSelected : '',
                        isBlocked ? cn.categoryListRowBlocked : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <div className={cn.categoryCheckboxCell}>
                        {isBlocked ? (
                          <Tooltip title={CONFLICT_TOOLTIP}>
                            <span>{checkbox}</span>
                          </Tooltip>
                        ) : (
                          checkbox
                        )}
                      </div>
                      <div className={cn.categoryCodeCell}>{node.code}</div>
                      <div className={cn.categoryNameCell}>
                        <div
                          className={cn.categoryTreeLabel}
                          style={{ '--tree-depth': node.depth }}
                        >
                          {hasChildren ? (
                            <button
                              type="button"
                              className={[
                                cn.categoryExpandButton,
                                isExpanded ? cn.categoryExpandButtonOpen : '',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              onClick={() => handleExpandToggle(node.id)}
                            >
                              <span className={cn.categoryExpandIcon}>
                                {'>'}
                              </span>
                            </button>
                          ) : (
                            <span className={cn.categoryExpandSpacer} />
                          )}
                          <div className={cn.categoryRowMain}>
                            <EllipsisTooltipText
                              className={cn.categoryRowTitle}
                              text={node.description}
                            />
                            {node.depth > 0 && (
                              <Tooltip title={node.path}>
                                <span className={cn.categoryRowPath}>
                                  {node.path}
                                </span>
                              </Tooltip>
                            )}
                            {isBlocked && (
                              <span className={cn.categoryConflictNote}>
                                Ветка недоступна.
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className={cn.categoryParentCell}>
                        {node.parentDescription || '—'}
                      </div>
                      <div className={cn.categoryStatusCell}>
                        {renderStatus(node.status)}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className={cn.categoryListEmpty}>Категории не найдены</div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export default CharacteristicCategoryBindingsModal
